import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const empty = {
  patente: '', alias: '', marca_modelo: '', tipo: 'Van', capacidad: 1, estado: 'activo'
};

export const VehiculoDialog = ({ open, onOpenChange, vehiculo, onSuccess }) => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (vehiculo) {
      setForm({
        patente: vehiculo.patente || '',
        alias: vehiculo.alias || '',
        marca_modelo: vehiculo.marca_modelo || '',
        tipo: vehiculo.tipo || 'Van',
        capacidad: vehiculo.capacidad || 1,
        estado: vehiculo.estado || 'activo'
      });
    } else {
      setForm(empty);
    }
  }, [vehiculo, open]);

  const handleChange = (name, value) => setForm((p) => ({ ...p, [name]: value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, capacidad: parseInt(form.capacidad) };
      if (vehiculo) {
        await axios.patch(`${API_URL}/api/vehiculos/${vehiculo.id}`, payload, { withCredentials: true });
        toast.success('Vehículo actualizado');
      } else {
        await axios.post(`${API_URL}/api/vehiculos`, payload, { withCredentials: true });
        toast.success('Vehículo creado');
      }
      onOpenChange(false);
      onSuccess();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-white border border-[#E9ECEF]">
        <DialogHeader>
          <DialogTitle className="text-2xl font-black font-['Chivo'] tracking-tight text-[#0A0A0A]">
            {vehiculo ? 'Editar Vehículo' : 'Nuevo Vehículo'}
          </DialogTitle>
          <DialogDescription className="text-[#495057] font-['IBM_Plex_Sans']">
            {vehiculo ? 'Modifica los datos del vehículo' : 'Registra un nuevo vehículo en la flota'}
          </DialogDescription>
        </DialogHeader>

        <form data-testid="vehiculo-form" onSubmit={submit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label className="font-['IBM_Plex_Sans'] font-medium text-[#0A0A0A]">Patente / Placa *</Label>
            <Input
              data-testid="veh-patente-input"
              value={form.patente}
              onChange={(e) => handleChange('patente', e.target.value)}
              placeholder="ABC-1234"
              className="border-[#E9ECEF] focus:border-[#C1121F] font-['IBM_Plex_Sans'] uppercase"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="font-['IBM_Plex_Sans'] font-medium text-[#0A0A0A]">Alias</Label>
              <Input
                data-testid="veh-alias-input"
                value={form.alias}
                onChange={(e) => handleChange('alias', e.target.value)}
                placeholder="Van #05"
                className="border-[#E9ECEF] focus:border-[#C1121F] font-['IBM_Plex_Sans']"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-['IBM_Plex_Sans'] font-medium text-[#0A0A0A]">Capacidad *</Label>
              <Input
                data-testid="veh-capacidad-input"
                type="number"
                min="1"
                value={form.capacidad}
                onChange={(e) => handleChange('capacidad', e.target.value)}
                className="border-[#E9ECEF] focus:border-[#C1121F] font-['IBM_Plex_Sans']"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="font-['IBM_Plex_Sans'] font-medium text-[#0A0A0A]">Marca y Modelo</Label>
            <Input
              data-testid="veh-marca-input"
              value={form.marca_modelo}
              onChange={(e) => handleChange('marca_modelo', e.target.value)}
              placeholder="Toyota Hiace 2023"
              className="border-[#E9ECEF] focus:border-[#C1121F] font-['IBM_Plex_Sans']"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="font-['IBM_Plex_Sans'] font-medium text-[#0A0A0A]">Tipo *</Label>
              <Select value={form.tipo} onValueChange={(v) => handleChange('tipo', v)}>
                <SelectTrigger data-testid="veh-tipo-select" className="border-[#E9ECEF] focus:border-[#C1121F] font-['IBM_Plex_Sans']">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Van">Van</SelectItem>
                  <SelectItem value="Camioneta">Camioneta</SelectItem>
                  <SelectItem value="Auto">Auto</SelectItem>
                  <SelectItem value="Ambulancia">Ambulancia</SelectItem>
                  <SelectItem value="Camion">Camión</SelectItem>
                  <SelectItem value="Otro">Otro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="font-['IBM_Plex_Sans'] font-medium text-[#0A0A0A]">Estado *</Label>
              <Select value={form.estado} onValueChange={(v) => handleChange('estado', v)}>
                <SelectTrigger data-testid="veh-estado-select" className="border-[#E9ECEF] focus:border-[#C1121F] font-['IBM_Plex_Sans']">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="activo">Activo</SelectItem>
                  <SelectItem value="mantenimiento">En mantenimiento</SelectItem>
                  <SelectItem value="baja">De baja</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1 border-[#E9ECEF] hover:bg-[#F8F9FA] font-['IBM_Plex_Sans']"
            >
              Cancelar
            </Button>
            <Button
              data-testid="veh-submit-button"
              type="submit"
              disabled={loading}
              className="flex-1 bg-[#C1121F] hover:bg-[#8B0000] text-white font-['IBM_Plex_Sans'] transition-colors"
            >
              {loading ? 'Guardando...' : (vehiculo ? 'Guardar cambios' : 'Crear vehículo')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
